import { useEffect, useRef } from 'react';

import { Button } from '@opendoor/atoms/button';
import { FavoriteHeartIcon } from 'components/marketplace/shared/FavoriteHeartIcon';

import styles from './PdpFavoriteButton.module.css';

// Match playFavoriteHeartSparkle in iOS ListingAnnotationView.swift.
const PARTICLES = [
  { angle: -100, startRadius: 8.5, travel: 17.6, size: 3.75, delay: 0, travelDuration: 320 },
  { angle: -54, startRadius: 7.5, travel: 13.6, size: 2.5, delay: 20, travelDuration: 280 },
  { angle: -18, startRadius: 8, travel: 20, size: 3.5, delay: 10, travelDuration: 370 },
  { angle: 26, startRadius: 6.5, travel: 15.2, size: 3, delay: 30, travelDuration: 310 },
  { angle: 70, startRadius: 8.5, travel: 18.4, size: 4, delay: 0, travelDuration: 400 },
  { angle: 118, startRadius: 7, travel: 12.8, size: 2.25, delay: 40, travelDuration: 260 },
  { angle: 154, startRadius: 8, travel: 16.8, size: 3.25, delay: 20, travelDuration: 340 },
  { angle: 202, startRadius: 6.5, travel: 14.4, size: 2.75, delay: 0, travelDuration: 300 },
  { angle: 242, startRadius: 8.5, travel: 19.2, size: 3.5, delay: 30, travelDuration: 370 },
  { angle: 294, startRadius: 7, travel: 13.6, size: 3, delay: 10, travelDuration: 290 },
].map((particle) => {
  const radians = (particle.angle * Math.PI) / 180;
  const translate = (radius: number) =>
    `translate(${Math.cos(radians) * radius}px, ${Math.sin(radians) * radius}px)`;
  return {
    ...particle,
    start: translate(particle.startRadius),
    end: translate(particle.startRadius + particle.travel),
  };
});

type PdpFavoriteButtonProps = {
  className: string;
  isLiked: boolean;
  onToggleFavorite: () => void;
};

export const PdpFavoriteButton = ({
  className,
  isLiked,
  onToggleFavorite,
}: PdpFavoriteButtonProps) => {
  const heartRef = useRef<HTMLSpanElement>(null);
  const particleRefs = useRef<Array<HTMLSpanElement | null>>([]);
  const animateNextChange = useRef(false);

  useEffect(() => {
    const heart = heartRef.current;
    if (!animateNextChange.current || !heart) return undefined;
    animateNextChange.current = false;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const animations = [
      heart.animate(
        reduceMotion
          ? [
              { transform: 'scale(1)', offset: 0 },
              { transform: 'scale(0.94)', offset: 100 / 240 },
              { transform: 'scale(1)', offset: 1 },
            ]
          : [
              { transform: 'scale(1)', offset: 0, easing: 'ease-out' },
              {
                transform: 'scale(0.82)',
                offset: 60 / 380,
                easing: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
              },
              { transform: 'scale(1.2)', offset: 200 / 380, easing: 'ease-out' },
              { transform: 'scale(1)', offset: 1 },
            ],
        { duration: reduceMotion ? 240 : 380 },
      ),
    ];

    if (isLiked && !reduceMotion) {
      PARTICLES.forEach((particle, index) => {
        const element = particleRefs.current[index];
        if (!element) return;
        const duration = particle.travelDuration + 100;
        const endOffset = particle.travelDuration / duration;
        animations.push(
          element.animate(
            [
              { transform: particle.start, opacity: 0, offset: 0 },
              { opacity: 1, offset: 0.08 },
              { transform: particle.end, opacity: 1, offset: endOffset },
              { transform: particle.end, opacity: 0, offset: 1 },
            ],
            { delay: 140 + particle.delay, duration, easing: 'ease-out' },
          ),
        );
      });
    }

    return () => animations.forEach((animation) => animation.cancel());
  }, [isLiked]);

  return (
    <Button
      variant="secondary"
      size="md"
      analyticsName="pdp-refresh-favorite"
      aria-label={isLiked ? 'unfavorite' : 'favorite'}
      aria-pressed={isLiked}
      onClick={() => {
        animateNextChange.current = true;
        onToggleFavorite();
      }}
      className={`group/favorite ${className}`}
    >
      <span className={styles.animation}>
        <span ref={heartRef} className={styles.heart}>
          <FavoriteHeartIcon filled={isLiked} interactive size={16} />
        </span>
        {PARTICLES.map((particle, index) => (
          <span
            key={particle.angle}
            aria-hidden="true"
            ref={(element) => {
              particleRefs.current[index] = element;
            }}
            className={styles.particle}
            style={{ width: particle.size * 0.8, height: particle.size * 0.8 }}
          />
        ))}
      </span>
    </Button>
  );
};
